import { SkeletonProps } from '@chakra-ui/react';
import { IQuoteOfferingRespSchema } from '@monetize/types/app';
import { Fragment } from 'react';
import { MBox, MFlex, MGrid, MSkeleton } from '../../../../components/Monetize';
import { QUOTE_GRID_TEMPLATE_COLUMNS } from '../../../../constants/quotes';
import { QuoteFormOfferingTableHeader } from '../../Quote/QuoteFormOfferingTableHeader';
import { planQuoteOfferingsSkeleton } from './quoteInternalOfferingsSkeleton.utils';

interface QuoteInternalOfferingsSkeletonProps {
  /** Every offering on the quote, parents and scheduled-change segments alike */
  quoteOfferings: IQuoteOfferingRespSchema[];
  /** Editable offerings reserve a description line even when empty */
  canEditDescriptions?: boolean;
  /** The amendment layout splits offerings across two titled sections */
  useAmendmentV2?: boolean;
}

/** Collapsed offering header strip */
const HEADER_HEIGHT = '3rem';
/** Expanded header strip with the description line beneath the name */
const HEADER_WITH_DESCRIPTION_HEIGHT = '5.25rem';
/** The rates expand/collapse row an offering with scheduled changes renders */
const EXPAND_ROW_HEIGHT = '2.5rem';
/** A rate row or a product row in the expanded body */
const BODY_ROW_HEIGHT = '4.5rem';

/** Text-sized bars: a primary line reads like a value, a secondary like a label */
const PRIMARY_LINE = '1rem';
const SECONDARY_LINE = '0.75rem';

const SKELETON_COLORS = {
  startColor: 'tGray.support',
  endColor: 'tWhite.titanWhite',
};

const Bar = (props: SkeletonProps) => (
  <MSkeleton
    height={PRIMARY_LINE}
    borderRadius="xs"
    {...SKELETON_COLORS}
    {...props}
  />
);

/** Header strip: chevron, name (and description), action pills on the right */
const HeaderStrip = ({ showsDescription }: { showsDescription: boolean }) => (
  <MFlex
    h={showsDescription ? HEADER_WITH_DESCRIPTION_HEIGHT : HEADER_HEIGHT}
    bg="bg.muted"
    borderRadius="md"
    px="1"
    py={2}
    alignItems={showsDescription ? 'flex-start' : 'center'}
    justifyContent="space-between"
  >
    <MFlex alignItems="flex-start" gap={2}>
      <Bar w="3" h="3" mt="1" ml="2" />
      <MFlex direction="column" gap={2} pt="1">
        <Bar w="12rem" />
        {showsDescription && <Bar w="18rem" h={SECONDARY_LINE} />}
      </MFlex>
    </MFlex>
    <MFlex gap={3} alignItems="center" pr={2} pt={showsDescription ? 1 : 0}>
      <Bar w="4rem" />
      <Bar w="4rem" />
    </MFlex>
  </MFlex>
);

/** Rate row: three label/value stacks (Rate, Start Date, End Date) and a Prices pill */
const RateRow = () => (
  <MGrid
    templateColumns={QUOTE_GRID_TEMPLATE_COLUMNS}
    columnGap={6}
    h={BODY_ROW_HEIGHT}
    alignItems="center"
    pl={7}
    pr={2}
  >
    <MFlex gridColumn="1 / 3" alignItems="center" gap={6}>
      <Bar w="3" h="3" mr="-2" />
      {[10, 6, 6].map((widthRem, index) => (
        <MFlex key={index} direction="column" gap={2}>
          <Bar w="3.5rem" h={SECONDARY_LINE} />
          <Bar w={`${widthRem}rem`} />
        </MFlex>
      ))}
    </MFlex>
    <MFlex gridColumn="6 / -1" justifyContent="flex-end">
      <Bar w="4rem" />
    </MFlex>
  </MGrid>
);

/** Product row: name and description, then quantity, price per unit and amount */
const ItemRow = () => (
  <MGrid
    templateColumns={QUOTE_GRID_TEMPLATE_COLUMNS}
    columnGap={6}
    h={BODY_ROW_HEIGHT}
    alignItems="center"
    pl={7}
    pr={2}
    borderBottomWidth="1px"
    borderBottomStyle="solid"
    borderBottomColor="border.subtle"
  >
    <MFlex direction="column" gap={2} pl={6} pr={12}>
      <Bar w="55%" />
      <Bar w="35%" h={SECONDARY_LINE} />
    </MFlex>
    <MBox />
    <MFlex justifyContent="center">
      <Bar w="2.5rem" />
    </MFlex>
    <MBox />
    <MFlex justifyContent="flex-end">
      <Bar w="5.5rem" />
    </MFlex>
    <MFlex justifyContent="flex-end">
      <Bar w="5rem" h="1.125rem" />
    </MFlex>
  </MGrid>
);

/**
 * Stand-in for the offerings table while the catalog and rate data it needs
 * are still loading. Offerings mount expanded, so each block reserves roughly
 * the expanded height: a header strip, then one placeholder row per rate
 * segment and per product, at the real rows' heights and on the real column
 * grid. The swap to the live table then barely moves the page.
 */
export const QuoteInternalOfferingsSkeleton = ({
  quoteOfferings,
  canEditDescriptions = false,
  useAmendmentV2 = false,
}: QuoteInternalOfferingsSkeletonProps) => {
  const blocks = planQuoteOfferingsSkeleton(quoteOfferings, {
    canEditDescriptions,
  });

  /**
   * The amendment layout renders the contract's offerings and the amendment's
   * own under separate headers, so the skeleton splits the same way. Every
   * other layout is one run under one header.
   */
  const sections = useAmendmentV2
    ? [
        {
          title: 'Existing Offerings',
          headerProps: { mt: '2' },
          blocks: blocks.filter(
            ({ isAddedToAmendment }) => !isAddedToAmendment,
          ),
        },
        {
          title: 'New Offerings',
          headerProps: { mt: '8' },
          blocks: blocks.filter(({ isAddedToAmendment }) => isAddedToAmendment),
        },
      ]
    : [{ title: undefined, headerProps: {}, blocks }];

  // Index across sections, so ids stay unique on the page
  let blockIndex = -1;

  return (
    <MBox data-testid="quote-offerings-skeleton" aria-busy="true">
      {/* Expand all, and Manage Groups on the right */}
      {blocks.length >= 2 && (
        <MFlex justify="space-between" align="center" mb={2} h="2rem">
          <Bar w="5rem" h={SECONDARY_LINE} />
          <Bar w="7rem" h={SECONDARY_LINE} />
        </MFlex>
      )}

      {sections.map(({ title, headerProps, blocks: sectionBlocks }) => (
        <MBox key={title ?? 'all'}>
          <QuoteFormOfferingTableHeader
            offeringTitle={title}
            {...headerProps}
          />
          {sectionBlocks.length === 0 && !useAmendmentV2 && (
            <MBox data-testid="quote-offerings-skeleton-row-0" mb={1}>
              <HeaderStrip showsDescription={false} />
            </MBox>
          )}
          {sectionBlocks.map(
            ({
              id,
              showsDescription,
              hasScheduledChanges,
              itemCountsPerSegment,
            }) => {
              blockIndex += 1;
              return (
                <MBox
                  key={id}
                  data-testid={`quote-offerings-skeleton-row-${blockIndex}`}
                >
                  <HeaderStrip showsDescription={showsDescription} />
                  {hasScheduledChanges && (
                    <MFlex h={EXPAND_ROW_HEIGHT} alignItems="center" pl={9}>
                      <Bar w="5rem" h={SECONDARY_LINE} />
                    </MFlex>
                  )}
                  {itemCountsPerSegment.map((itemCount, segmentIndex) => (
                    <Fragment key={segmentIndex}>
                      <RateRow />
                      {Array.from({ length: itemCount }, (_, itemIndex) => (
                        <ItemRow key={itemIndex} />
                      ))}
                    </Fragment>
                  ))}
                  <MBox h={1} />
                </MBox>
              );
            },
          )}
        </MBox>
      ))}
    </MBox>
  );
};
